import { Link } from "gatsby";
import { PropsWithChildren } from "react";
import tw, { styled } from "twin.macro";

import abTestingSrc from "../../assets/images/features/nav/ab-testing.svg";
import autopilotSrc from "../../assets/images/features/nav/autopilot.svg";
import customBrandingSrc from "../../assets/images/features/nav/custom-branding.svg";
import customerExitSurveysSrc from "../../assets/images/features/nav/customer-exit-surveys.svg";
import customerSegmentationSrc from "../../assets/images/features/nav/customer-segmentation.svg";
import easySetupSrc from "../../assets/images/features/nav/easy-setup.svg";
import featureItemAltBlackBgSrc from "../../assets/images/features/nav/feature-item-alt-black-bg.svg";
import featureItemAltBlueBgSrc from "../../assets/images/features/nav/feature-item-alt-blue-bg.svg";
import featureItemAltWhiteBgSrc from "../../assets/images/features/nav/feature-item-alt-white-bg.svg";
import offersAndDeflectionsSrc from "../../assets/images/features/nav/offers-and-deflections.svg";
import { SplitDescription as Subheadline } from "../common/SplitItems";
import SvgObject from "../elements/SvgObject";

const GridStandard = styled.div`
  ${tw`flex flex-row flex-wrap items-start justify-center text-center my-0 md:my-6`}

  --flex-gap: 30px;
  gap: var(--flex-gap);

  .feature-item {
    ${tw`block hover:no-underline pt-5`}

    --boxes-per-row: 3;
    --available-width: calc(100% - (var(--boxes-per-row) * var(--flex-gap)));

    flex-basis: calc(var(--available-width) / var(--boxes-per-row));

    @media (max-width: 950px) {
      --boxes-per-row: 2;

      p {
        ${tw`px-8`}
      }
    }

    @media (max-width: 650px) {
      --boxes-per-row: 1;

      p {
        ${tw`px-12 mb-0`}
      }
    }

    object {
      ${tw`mx-auto transition-all duration-200 w-full max-w-[270px]`}
      box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.09);
      border-radius: 15px;
    }

    h4.feature-headline {
      ${tw`text-theme-blue-1 font-title font-medium self-stretch mt-4`}

      font-size: 24px;
      line-height: 32px;

      a {
        ${tw`text-theme-blue-1`}
      }
    }

    &:nth-child(even) > object {
      background: #dee6ea;
    }

    &:nth-child(odd) > object {
      ${tw`bg-theme-blue-1`}
    }
  }

  // Only do hover effects if it's an anchor tag
  a.feature-item:hover {
    text-decoration: none !important;

    object {
      box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.15);
      transform: scale(1.03);
      pointer-events: none;
    }
  }
`;

const GridAlt = styled.div`
  ${tw`flex flex-row flex-wrap items-start justify-center my-0 md:my-6`}

  --flex-gap: 30px;
  gap: var(--flex-gap);

  .feature-item {
    ${tw`block hover:no-underline flex flex-row items-center justify-start gap-[20px] py-[40px] px-[20px] rounded-[30px]`}
    ${tw`transition-all duration-200`}

    --boxes-per-row: 2;
    --available-width: calc(100% - (var(--boxes-per-row) * var(--flex-gap)));

    flex-basis: calc(var(--available-width) / var(--boxes-per-row));

    @media (max-width: 1023px) {
      --boxes-per-row: 1;
      ${tw`max-w-[509px] flex-col text-center`}

      &:nth-child(3) {
        ${tw`order-4`}
      }
    }

    object {
      ${tw`w-full max-w-[170px] z-1`}
    }

    h4.feature-headline {
      ${tw`block font-title font-semibold m-0 text-title`}

      font-size: 24px;
      line-height: 32px;
    }

    ${tw`bg-white relative overflow-hidden z-0`}
    background-image: url(${featureItemAltWhiteBgSrc});
    background-repeat: no-repeat;
    background-size: 100% 400px;
    background-position: bottom left;

    &:before {
      content: "";
      ${tw`block w-[293px] h-[295px] bg-white rounded-full absolute z-0`}
      ${tw`hidden lg:block lg:left-[-150px]`}
        top: calc(50% - 147.5px);
    }

    &:nth-child(1),
    &:nth-child(5) {
      ${tw`bg-theme-blue-1`}
      background-image: url(${featureItemAltBlueBgSrc});

      &:before {
        ${tw`bg-theme-blue-1`}
      }

      h4.feature-headline {
        ${tw`text-white`}
      }
    }

    &:nth-child(4),
    &:nth-child(7) {
      ${tw`bg-[#070B15]`}
      background-image: url(${featureItemAltBlackBgSrc});

      &:before {
        ${tw`bg-[#090F1A]`}
      }

      h4.feature-headline {
        ${tw`text-white`}
      }
    }
  }

  // Only do hover effects if it's an anchor tag
  a.feature-item:hover {
    text-decoration: none !important;
    box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.15);
    transform: scale(1.03);

    object {
      pointer-events: none;
    }
  }
`;

const Feature: React.FunctionComponent<
  PropsWithChildren<{ to: string; linkify: boolean }>
> = ({ to, linkify, children }) =>
  linkify ? (
    <Link to={to} className="feature-item">
      {children}
    </Link>
  ) : (
    <div className="feature-item">{children}</div>
  );

interface FeaturesProps {
  current?: string;
  linkify?: boolean;
  alt?: boolean;
}

const Features: React.FunctionComponent<FeaturesProps> = ({
  current,
  linkify = false,
  alt = false,
}) => {
  const Grid = alt ? GridAlt : GridStandard;
  return (
    <Grid>
      {current !== "easySetup" && (
        <Feature to="/features/easy-setup/" linkify={linkify}>
          <SvgObject data={easySetupSrc} width={270} height={190} />
          <h4 className="feature-headline">
            {alt
              ? "Easy setup, painless integration"
              : "Easy setup and integration"}
          </h4>
          {!alt && (
            <Subheadline>
              Up and running fast with low- and no-code options.
            </Subheadline>
          )}
        </Feature>
      )}
      {current !== "offersAndDeflections" && (
        <Feature to="/features/offers-and-deflections/" linkify={linkify}>
          <SvgObject data={offersAndDeflectionsSrc} width={270} height={190} />
          <h4 className="feature-headline">
            {alt
              ? "Personalized incentives, max retention"
              : "Offers and deflections"}
          </h4>
          {!alt && (
            <Subheadline>
              Create targeted, personalized incentives that really work.
            </Subheadline>
          )}
        </Feature>
      )}
      {current !== "customerExitSurveys" && (
        <Feature to="/features/customer-exit-surveys/" linkify={linkify}>
          <SvgObject data={customerExitSurveysSrc} width={270} height={190} />
          <h4 className="feature-headline">
            {alt ? "Actionable churn feedback, more saves" : "Exit surveys"}
          </h4>
          {!alt && (
            <Subheadline>
              Get actionable feedback and identify savable subscribers.
            </Subheadline>
          )}
        </Feature>
      )}
      {current !== "customBranding" && (
        <Feature to="/features/custom-branding/" linkify={linkify}>
          <SvgObject data={customBrandingSrc} width={270} height={190} />
          <h4 className="feature-headline">
            {alt ? "Custom branding for seamless UX" : "Custom branding"}
          </h4>
          {!alt && (
            <Subheadline>
              Customize every detail for a seamless user experience.
            </Subheadline>
          )}
        </Feature>
      )}
      {current !== "abTesting" && (
        <Feature to="/features/ab-testing/" linkify={linkify}>
          <SvgObject data={abTestingSrc} width={270} height={190} />
          <h4 className="feature-headline">
            {alt
              ? "A/B testing + powerful reporting"
              : "A/B testing and insights"}
          </h4>
          {!alt && (
            <Subheadline>
              Test and learn your way to the most effective flow possible.
            </Subheadline>
          )}
        </Feature>
      )}
      {current !== "customerSegmentation" && (
        <Feature to="/features/customer-segmentation/" linkify={linkify}>
          <SvgObject data={customerSegmentationSrc} width={270} height={190} />
          <h4 className="feature-headline">
            {alt ? "Sophisticated customer segmentation" : "Segmentation"}
          </h4>
          {!alt && (
            <Subheadline>
              Individualize the cancellation experience for each subscriber.
            </Subheadline>
          )}
        </Feature>
      )}
      {current !== "autopilot" && (
        <Feature to="/features/autopilot-offers/" linkify={linkify}>
          <SvgObject data={autopilotSrc} width={270} height={190} />
          <h4 className="feature-headline">
            {alt ? "AI Autopilot offer optimization" : "Autopilot offers"}
          </h4>
          {!alt && (
            <Subheadline>
              Optimize offer performance with AI-powered experiments.
            </Subheadline>
          )}
        </Feature>
      )}
    </Grid>
  );
};

export default Features;
